import {Button} from '@wandb/components';
import React, {memo, useCallback} from 'react';
import {useHistory} from 'react-router-dom';

import {Analytics} from '../../services/analytics';
import {useDispatch} from '../../state/hooks';
import {createSweepWithRunSetArgs} from '../../state/sweeps/thunks';
import {type Ref as FilterRef} from '../../state/views/filter/types';
import {type Ref as GroupSelectionsRef} from '../../state/views/groupSelections/types';
import {type Ref as SortRef} from '../../state/views/sort/types';
import {type Ref as TempSelectionsRef} from '../../state/views/tempSelections/types';
import type {Grouping} from '../../util/queryTypes';
import type {ConfigV2} from '../../util/runfeed';
import type {GroupSelectionState} from '../../util/selectionmanager';
import {createSweep} from '../../util/urls/paths';
import {DeleteRunsButton} from '../DeleteRunsButton';
import {JobDrawerLocked} from '../Launch/JobDrawer/JobDrawer';
import type {LaunchDrawerJobRun} from '../Launch/utilTypes';
import {RunMover} from '../RunMover';
import BulkTagEditor from '../Tags/BulkTagEditor';
import {MoveRunDrawerBulkButton} from './MoveRunDrawer/MoveRunDrawerBulk';
import {useRunSelectorFeatureEnabled} from './RunSelectorFeaturesContext';
import {
  useCurrentRunSetRef,
  useRunSelectorProjectInfo,
} from './RunSelectorRunSetContext';
import {RunsFreezeAndFilter} from './RunsFreeze';
import {RunsGroupTableAction} from './RunsGrouping';
import {RunsSortTableAction} from './RunsSort';

type RunSelectorExtraTableActionsLeftProps = {
  closeLaunchConfigModal: () => void;
  enableFreezeRunset: boolean;
  enableSetProject: boolean;
  groupTempSelection: GroupSelectionState;
  hasEntityStorageEnabled: boolean;
  filtersRef: FilterRef;
  groupSelectionsRef: GroupSelectionsRef;
  isExpandable: boolean;
  isExpanded: boolean;
  isLaunchActive: boolean;
  isLaunchPublicQueueActive: boolean;
  launchDrawerJobRun: LaunchDrawerJobRun | null;
  onAddTag: (tag: string) => void;
  onCloseSortPopup: () => void;
  onDeleteArtifacts: (deleteArtifacts: boolean) => void;
  onMoveRuns: (entity: string, projectId: string) => Promise<string>;
  onMovedRuns: () => void;
  onOpenSortPopup: () => void;
  onReload: () => void;
  onRemoveTag: (tag: string) => void;
  recommendedGrouping: Grouping | undefined;
  resetPagination: () => void;
  setProject: (
    newEntityName: string,
    newProjectName: string,
    newProjectId: string
  ) => void;
  sortPopupOpen: boolean;
  sortRef: SortRef;
  tableSettings: ConfigV2;
  tags: {name: string; colorIndex: number | undefined; count: number}[];
  tempOutputArtifactsCount: number;
  tempSelectedActiveCount: number;
  tempSelectedCount: number;
  tempSelectionsRef: TempSelectionsRef;
  combinedFilter: any;
};

export function RunSelectorExtraTableActionsLeft(
  props: RunSelectorExtraTableActionsLeftProps
) {
  const isEnabled = useRunSelectorFeatureEnabled();
  const {projectName, entityName, projectInternalId} =
    useRunSelectorProjectInfo();

  return (
    <>
      {(props.isLaunchActive || props.isLaunchPublicQueueActive) && (
        <JobDrawerLocked
          open={props.launchDrawerJobRun != null}
          closeDrawer={props.closeLaunchConfigModal}
          entityName={entityName}
          projectName={projectName}
          jobId={props.launchDrawerJobRun?.jobId ?? ''}
          jobVersion={props.launchDrawerJobRun?.jobVersion ?? ''}
          defaultRunToClone={props.launchDrawerJobRun?.runName}
        />
      )}
      {isEnabled('filter') && (
        <RunsFreezeAndFilter
          enableFreezeRunset={props.enableFreezeRunset}
          filtersRef={props.filtersRef}
          entityName={entityName}
          projectName={projectName}
          compact={props.isExpandable && !props.isExpanded}
          onChange={() => {
            props.resetPagination();
          }}
          tableSettings={props.tableSettings}
        />
      )}
      {isEnabled('group') && (
        <RunsGroupTableAction
          entityName={entityName}
          projectName={projectName}
          groupSelectionsRef={props.groupSelectionsRef}
          recommendedGrouping={props.recommendedGrouping}
          compact={props.isExpandable && !props.isExpanded}
          onGroupingChanged={() => {
            props.resetPagination();
          }}
          tableSettings={props.tableSettings}
        />
      )}
      {isEnabled('sort') && (
        <RunsSortTableAction
          entityName={entityName}
          projectName={projectName}
          sortRef={props.sortRef}
          compact={props.isExpandable && !props.isExpanded}
          open={props.sortPopupOpen}
          onOpen={() => props.onOpenSortPopup()}
          onClose={() => props.onCloseSortPopup()}
          onSortChanged={() => props.resetPagination()}
          tableSettings={props.tableSettings}
        />
      )}
      {props.isExpandable && !props.isExpanded ? (
        <></>
      ) : (
        <>
          {/* BATCH TAGGING, MOVING, DELETING */}
          {isEnabled('tag') && (
            <BulkTagEditor
              // TODO(views): Here and below, this is a bad way to pass these counts through as it
              // won't always update.
              selectedCount={props.tempSelectedCount}
              tags={props.tags}
              addTag={props.onAddTag}
              removeTag={props.onRemoveTag}
            />
          )}
          {isEnabled('move') && props.tempSelectedCount ? (
            <MoveRunDrawerBulkButton
              entityName={entityName}
              groupTempSelection={props.groupTempSelection}
              onMovedRuns={props.onMovedRuns}
              projectName={projectName}
              groupSelectionsRef={props.groupSelectionsRef}
              selectedCount={props.tempSelectedCount}
            />
          ) : null}

          {isEnabled('move') && projectInternalId && (
            <RunMover
              entityName={entityName}
              // prevent users from trying to move runs to another entity if the entity is using external storage
              // because this operation does not move the run files
              requireSameEntity={props.hasEntityStorageEnabled}
              requireSameEntityReason={
                "Can't move runs between entities when using external storage"
              }
              projectInternalId={projectInternalId}
              projectName={projectName}
              activeRunsCount={props.tempSelectedActiveCount}
              selectedCount={props.tempSelectedCount}
              refetch={props.onReload}
              onMove={props.onMoveRuns}
            />
          )}
          {isEnabled('delete') && (
            <DeleteRunsButton
              selectedCount={props.tempSelectedCount}
              onDelete={props.onDeleteArtifacts}
              outputArtifactsCount={props.tempOutputArtifactsCount}
              activeRunsCount={props.tempSelectedActiveCount}
              projectName={projectName}
              entityName={entityName}
              combinedFilter={props.combinedFilter}
            />
          )}
          {isEnabled('create-sweep') && (
            <CreateSweepButton tempSelectionsRef={props.tempSelectionsRef} />
          )}
        </>
      )}
    </>
  );
}

const CreateSweepButton = memo(
  ({tempSelectionsRef}: {tempSelectionsRef: TempSelectionsRef}) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const runSetRef = useCurrentRunSetRef();
    const {entityName, projectName} = useRunSelectorProjectInfo();

    const onCreateSweepPress = useCallback(() => {
      Analytics.track('Create Sweep Clicked', {
        entityName,
        projectName,
        location: 'runs table',
      });

      dispatch(createSweepWithRunSetArgs(runSetRef, tempSelectionsRef));

      history.push(createSweep({entityName, projectName}));
    }, [
      dispatch,
      entityName,
      history,
      projectName,
      runSetRef,
      tempSelectionsRef,
    ]);

    return (
      <Button
        startIcon="sweeps-broom"
        variant="secondary"
        onPress={onCreateSweepPress}>
        New sweep
      </Button>
    );
  }
);
